import React, { useEffect, useState } from 'react';
import { Modal } from '@/components';
import { useForm, useController } from 'react-hook-form';
import { BoxV2 as Box, Flex, Button, TextInput } from 'portal-commons';
import {
  isValidInput,
  createMaxLengthValidator,
  createRequiredValidator,
} from '@/utils/validator';
import { CampaignDetail, CampaignOperationItem } from '../../types';
import { campaignController } from '@/apis/core';
import { toastFlashMessage } from '@/utils';

interface Props {
  open: boolean;
  campaign: CampaignDetail;
  carrier: CampaignOperationItem;
  isRedacted: boolean;
  onClose: (reload: boolean) => void;
}

interface FormValues {
  explanation: string;
}

const CarrierLiftSuspensionModal: React.FC<Props> = ({
  open,
  campaign,
  carrier,
  isRedacted,
  onClose,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const { control, reset, handleSubmit } = useForm<FormValues>({
    mode: 'onTouched',
    defaultValues: {
      explanation: '',
    },
  });

  const {
    field: {
      value: explanation,
      onChange: setExplanation,
      onBlur: validateExplanation,
    },
    fieldState: { error: explanationError },
  } = useController({
    control,
    name: 'explanation',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please enter explanation'),
        maxLength: createMaxLengthValidator(3000),
        pattern: (value) => isValidInput(value) || 'Invalid input',
      },
    },
  });

  const onValidHandler = async (data: FormValues) => {
    setIsProcessing(true);
    const result = await campaignController.updateCampaignOperationStatus(
      {
        campaignUid: campaign.uid,
        networkId: carrier.networkId,
        status: 'REGISTERED',
      },
      data
    );
    if (result) {
      toastFlashMessage(
        `${carrier.rulesEngineResultsJson.mno} suspension successfully lifted`,
        'success'
      );
      onClose(true);
    }

    setIsProcessing(false);
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  return (
    <Modal open={open} testid="carrierLiftSuspensionModal">
      <Box
        as="h3"
        sx={{
          py: 's',
          fontSize: 'H600',
          fontWeight: 600,
          lineHeight: 'normal',
          color: 't.black80',
          textAlign: 'center',
        }}
      >
        Lift Suspension
      </Box>
      <form noValidate onSubmit={handleSubmit(onValidHandler)}>
        <Flex sx={{ flexDirection: 'column', gap: 'm', mt: 15 }}>
          <Box
            as="span"
            sx={{
              fontSize: 'H300',
              fontWeight: 400,
              color: 't.black80',
            }}
          >
            * Indicates a Required Field
          </Box>
          <TextInput
            readOnly
            label="Campaign ID"
            value={campaign.uid || 'N/A'}
          />
          <Flex sx={{ gap: 's' }}>
            <TextInput
              readOnly
              label="Brand ID"
              value={campaign.brand.uid || 'N/A'}
              style={{ width: '124px' }}
            />
            <TextInput
              readOnly
              label="Brand DBA Name"
              value={
                isRedacted
                  ? 'Redacted due to PII'
                  : campaign.brand.displayName || 'N/A'
              }
              style={{ flexGrow: 1 }}
            />
          </Flex>
          <Flex sx={{ gap: 's' }}>
            <TextInput
              readOnly
              label="CSP ID"
              value={campaign.brand.csp.uid || 'N/A'}
              style={{ width: '124px' }}
            />
            <TextInput
              readOnly
              label="CSP DBA Name"
              value={campaign.brand.csp.displayName || 'N/A'}
              style={{ flexGrow: 1 }}
            />
          </Flex>
          <TextInput
            readOnly
            label="Carrier"
            value={carrier.rulesEngineResultsJson?.mno || '--'}
          />
          <TextInput
            required
            multiline
            maxRows={4}
            data-testid="liftSuspensionExplanation"
            label="Explanation"
            placeholder="Please enter Explanation"
            disabled={isProcessing}
            value={explanation}
            error={!!explanationError?.message}
            note={explanationError?.message}
            onChange={setExplanation}
            onBlur={validateExplanation}
          />
          <Flex sx={{ py: 30, gap: 18, justifyContent: 'center' }}>
            <Button
              variant="outline"
              data-testid="carrierLiftSuspensionCancelButton"
              disabled={isProcessing}
              onClick={() => onClose(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={isProcessing}
              data-testid="carrierLiftSuspensionLiftButton"
              type="submit"
            >
              Lift Suspension
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
};

export default CarrierLiftSuspensionModal;
